import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Store, ArrowRight, X, CheckCircle2 } from 'lucide-react';
import { useLocale } from '@/contexts/LocaleContext';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OnboardingBanner = () => {
  const navigate = useNavigate();
  const { isUSA } = useLocale();
  const { token } = useAuth();
  
  const [dismissed, setDismissed] = useState(false);
  const [storeStatus, setStoreStatus] = useState({
    hasStore: false,
    productCount: 0,
    loading: true
  });

  useEffect(() => {
    // Check if user dismissed the banner
    const wasDismissed = localStorage.getItem('texhub_onboarding_dismissed');
    if (wasDismissed) {
      setDismissed(true);
    }

    // Check store status
    const checkStore = async () => {
      if (!token) return;
      try {
        const res = await fetch(`${API_URL}/api/stores/my`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setStoreStatus({
            hasStore: !!data?.store_id,
            productCount: data?.products_count || data?.product_count || 0,
            loading: false
          });
        } else {
          setStoreStatus({ hasStore: false, productCount: 0, loading: false });
        }
      } catch (e) {
        setStoreStatus({ hasStore: false, productCount: 0, loading: false });
      }
    };
    checkStore();
  }, [token]);

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('texhub_onboarding_dismissed', 'true');
  };

  const handleStartOnboarding = () => {
    navigate('/onboarding');
  };

  // Don't show if dismissed or still loading
  if (dismissed || storeStatus.loading) return null;
  
  // Calculate progress
  let progress = 0;
  let message = '';
  let showBanner = false;

  if (!storeStatus.hasStore) {
    progress = 10;
    message = isUSA ? 'Create your store to start receiving clients' : 'Crea tu tienda para empezar a recibir clientes';
    showBanner = true;
  } else if (storeStatus.productCount < 3) {
    progress = 50;
    message = isUSA 
      ? `Add ${3 - storeStatus.productCount} more products for better visibility` 
      : `Agrega ${3 - storeStatus.productCount} productos más para mejor visibilidad`;
    showBanner = true;
  }

  if (!showBanner) return null;

  return (
    <Card className="bg-gradient-to-r from-emerald-500 to-emerald-600 border-0 mb-6 relative overflow-hidden">
      {/* Dismiss button */}
      <button 
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-white/70 hover:text-white"
      >
        <X className="w-5 h-5" />
      </button>

      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
          {/* Icon */}
          <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
            <Store className="w-7 h-7 text-white" />
          </div>

          {/* Content */}
          <div className="flex-1">
            <h3 className="text-lg font-bold text-white mb-1">
              {isUSA ? 'Complete your store setup' : 'Completa tu tienda'}
            </h3>
            <p className="text-emerald-100 text-sm mb-3">
              {message}
            </p>
            
            {/* Progress bar */}
            <div className="flex items-center gap-3 mb-1">
              <Progress value={progress} className="h-2 flex-1 bg-white/30" />
              <span className="text-white text-sm font-medium">{progress}%</span>
            </div>
          </div>

          {/* CTA Button */}
          <Button 
            className="bg-white text-emerald-600 hover:bg-emerald-50 font-semibold"
            onClick={handleStartOnboarding}
          >
            {isUSA ? 'Continue' : 'Continuar'}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default OnboardingBanner;
